//#region IMPORTS
import { fromBackendDate } from "../../helpers/fromBackendDate";
import { useAppSelector } from "../../store/configureStore";
import { downloadFile } from "../../helpers/downloadFile";
import { InstoreOrder } from "../../models/instoreOrder";
import { ORDERSTATUS } from "../../models/order";
import { useHistory } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { format } from "date-fns";
import { toast } from "react-toastify";
import InstoreOrdersTableRowTrackAndTraceMenu from "./InstoreOrdersTableRowTrackAndTraceMenu";
import InstoreOrdersTableRowActionApproveToggle from "./InstoreOrdersTableRowActionApproveToggle";
import InstoreOrdersTableRowActionApprove from "./InstoreOrdersTableRowActionApprove";
import InstoreOrdersTableRowActionCancel from "./InstoreOrdersTableRowActionCancel";
import InstoreOrdersTableRowActionExport from "./InstoreOrdersTableRowActionExport";
import InstoreOrdersTableRowActionInProgress from "./InstoreOrdersTableRowActionInProgress";
import InstoreOrdersTableRowActionEdit from "./InstoreOrdersTableRowActionEdit";
import InstoreOrdersTableRowActionShip from "./InstoreOrdersTableRowActionShip";
import MainTableRowActionDelete from "../_layout/MainTableRowActionDelete";
import MainTableRowActionRead from "../_layout/MainTableRowActionRead";
import OrderStatusControl from "../orders/OrderStatusControl";
import usePickerManager from "../../hooks/usePickerManager";
import OrderTypeBadge from "../orders/OrderTypeBadge";
import useHQmanager from "../../hooks/useHQmanager";
import classNames from "../../helpers/classNames";
import agent from "../../api/agent";
//#endregion

//#region INTERFACE
interface Props {
  changeOrderStatusHandler: (
    instoreOrder: InstoreOrder,
    status: string
  ) => void;
  onToggleSelected?: () => void;
  deleteHandler: () => void;
  enableSelection?: boolean;
  instoreOrder: InstoreOrder;
  isSelected?: boolean;
  isAdmin?: boolean;
  isShop?: boolean;
  showActions?: boolean;
  showDecline?: boolean;
}
//#endregion

export default function InstoreOrdersTableRow({
  changeOrderStatusHandler,
  onToggleSelected,
  deleteHandler,
  enableSelection = false,
  instoreOrder,
  isSelected = false,
  isAdmin = false,
  isShop = false,
  showActions = false,
  showDecline = false,
}: Props) {
  //#region SETUP
  // Router
  const history = useHistory();

  // Redux
  const { filters } = useAppSelector((state) => state.orders);
  const { user } = useAppSelector((state) => state.account);

  // Roles
  const isHQ = useHQmanager(user);
  const isPicker = usePickerManager(user);
  //#endregion

  //#region LOGIC
  const exportInstoreOrderHandler = async () => {
    try {
      let blobFromFetch: Blob;
      const fileName = `InstoreOrder__${instoreOrder.order_code}__${
        instoreOrder.store_name
      }__${format(new Date(), "dd-MM-yyyy")}`;

      const response = await toast.promise(
        agent.InstoreOrders.export(instoreOrder.id),
        {
          pending: "Generating export data",
          success: "Download succeeded",
          error: "Something went wrong",
        }
      );

      blobFromFetch = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      downloadFile({
        blobFromFetch,
        extension: "xlsx",
        fileName,
      });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unexpected error while downloading";
      toast.error(message);
    }
  };

  const detailHandler = () => {
    history.push(`/instoreorders/${instoreOrder.id}`, true);
  };
  //#endregion

  //#region RENDER
  return (
    <tr className="border-t border-slate-300 h-10">
      {/* Checkbox */}
      {enableSelection && (
        <td className="pr-2 text-left text-xs text-gray-500 sm:pl-0 w-8">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-600"
            checked={isSelected}
            onChange={onToggleSelected}
          />
        </td>
      )}

      {/* read */}
      <td className="whitespace-nowrap px-2 text-left text-xs flex items-center h-10 gap-2 text-gray-500 sm:pl-0">
        <MainTableRowActionRead detailHandler={detailHandler} />
        {isAdmin ? (
          <InstoreOrdersTableRowActionEdit instoreOrder={instoreOrder} />
        ) : null}
      </td>

      {/* external_store_id */}
      {!isShop && (
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
          {instoreOrder.external_store_id ?? "Not found"}
        </td>
      )}

      {/* order_date */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {instoreOrder.order_date}
      </td>

      {/* order_code */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {instoreOrder.order_code}
      </td>

      {/* order_type_name */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        <OrderTypeBadge orderTypeName={instoreOrder.order_type_instore_name} />
      </td>

      {/* order_status_name */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        <OrderStatusControl
          currentStatus={instoreOrder.order_status_name}
          editable={isAdmin}
          orderStatusses={filters.order_status}
          changeOrderStatusHandler={(status: string) =>
            changeOrderStatusHandler(instoreOrder, status)
          }
        />
      </td>

      {/* store_language */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {instoreOrder.store_language}
      </td>

      {/* delivery_date */}
      {!isShop && (
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
          {fromBackendDate(instoreOrder.delivery_date)}
        </td>
      )}

      {/* store_opening_date */}
      {!isShop && (
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
          {fromBackendDate(instoreOrder.store_opening_date)}
        </td>
      )}

      {/* article_amount */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {instoreOrder.total_count}
      </td>

      {/* comment */}
      <td className="whitespace-nowrap px-2 text-left text-xs sm:pl-0">
        <div
          data-tooltip-content={
            instoreOrder.comment && instoreOrder.comment !== ""
              ? instoreOrder.comment.split(";").join(" - ")
              : null
          }
          data-tooltip-variant="info"
          data-tooltip-place="left"
          data-tooltip-id={instoreOrder.order_code + "extraprojects"}
          className={classNames(
            instoreOrder.comment && instoreOrder.comment !== ""
              ? "font-bold text-gray-900"
              : "text-gray-500"
          )}
        >
          {instoreOrder.comment && instoreOrder.comment !== ""
            ? `${instoreOrder.comment?.split(";").length} project${
                instoreOrder.comment?.split(";").length > 1 ? "s" : ""
              }`
            : "No extra projects"}
          {instoreOrder.comment && instoreOrder.comment !== "" ? (
            <Tooltip id={instoreOrder.order_code + "extraprojects"} />
          ) : null}
        </div>
      </td>

      {/* track_and_trace */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0 py-2 ">
        {instoreOrder.track_and_trace ? (
          <InstoreOrdersTableRowTrackAndTraceMenu
            parcels={instoreOrder.track_and_trace.split(";")}
          />
        ) : (
          <span className="py-1 text-sm text-gray-600">
            no tracking info available
          </span>
        )}
      </td>

      {/* Decline toggle (admin + picker, Approve preset only) */}
      {showDecline && (
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
          <InstoreOrdersTableRowActionApproveToggle
            instoreOrder={instoreOrder}
            hide={
              !(
                (isHQ || isAdmin || isPicker) &&
                instoreOrder.order_status_name === ORDERSTATUS.pending
              )
            }
          />
        </td>
      )}

      {/* Actions (approve / ship / cancel) */}
      {showActions && (
        <td className="whitespace-nowrap px-2 text-left text-sm text-gray-900">
          <div className="flex gap-2 items-center">
            {/* Export (admin + picker), left of all other actions */}
            <InstoreOrdersTableRowActionExport
              instoreOrder={instoreOrder}
              exportHandler={exportInstoreOrderHandler}
              hide={
                !(
                  (isAdmin || isPicker) &&
                  instoreOrder.order_status_name !== ORDERSTATUS.cancelled
                )
              }
            />
            <InstoreOrdersTableRowActionApprove
              instoreOrder={instoreOrder}
              approveHandler={() =>
                changeOrderStatusHandler(
                  instoreOrder,
                  instoreOrder.approved === false
                    ? ORDERSTATUS.cancelled
                    : ORDERSTATUS.ordered,
                )
              }
              hide={
                !(
                  (isHQ || isAdmin || isPicker) &&
                  instoreOrder.order_status_name === ORDERSTATUS.pending
                )
              }
            />
            {/* Set in progress (left of ship) */}
            <InstoreOrdersTableRowActionInProgress
              instoreOrder={instoreOrder}
              inProgressHandler={() =>
                changeOrderStatusHandler(instoreOrder, ORDERSTATUS.inProgress)
              }
              hide={
                !(
                  (isPicker || isAdmin) &&
                  instoreOrder.order_status_name === ORDERSTATUS.ordered
                )
              }
            />
            <InstoreOrdersTableRowActionShip
              instoreOrder={instoreOrder}
              shipHandler={() =>
                changeOrderStatusHandler(instoreOrder, ORDERSTATUS.shipped)
              }
              hide={
                !(
                  (isPicker || isAdmin) &&
                  instoreOrder.order_status_name === ORDERSTATUS.inProgress
                )
              }
            />
            <InstoreOrdersTableRowActionCancel
              instoreOrder={instoreOrder}
              cancelHandler={() =>
                changeOrderStatusHandler(instoreOrder, ORDERSTATUS.cancelled)
              }
              hide={
                !(
                  (isHQ || isAdmin || isPicker) &&
                  instoreOrder.order_status_name !== ORDERSTATUS.cancelled &&
                  instoreOrder.order_status_name !== ORDERSTATUS.shipped
                )
              }
            />
          </div>
        </td>
      )}

      {/* delete (admin only) */}
      {isAdmin && (
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
          <MainTableRowActionDelete
            deleteHandler={deleteHandler}
            tooltipId={`instoreOrder${instoreOrder.id}`}
          />
        </td>
      )}
    </tr>
  );
  //#endregion
}
